import type { Channel, Recurrence, Task, TaskPriority, TaskStatus, UserSummary } from '@nexo/types';
import {
  defaultTaskColumns,
  taskPriorities,
  taskPriorityLabels,
  taskStatuses,
  taskStatusLabels,
} from '@nexo/types';
import type { BadgeTone } from '@nexo/ui';
import { daysFromToday, dueState, parseDueDate } from '../../lib/format';
import { intlLocale, t } from '../../lib/i18n';

export const statusTone: Record<TaskStatus, BadgeTone> = {
  todo: 'neutral',
  in_progress: 'accent',
  done: 'success',
};

const priorityWeight: Record<TaskPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

/** Next status when clicking the status icon: todo → in progress → done → todo. */
export function nextStatus(status: TaskStatus): TaskStatus {
  return status === 'todo' ? 'in_progress' : status === 'in_progress' ? 'done' : 'todo';
}

/** Open tasks first, then soonest due date (undated last), then priority, then newest. */
export function sortTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const doneA = a.status === 'done' ? 1 : 0;
    const doneB = b.status === 'done' ? 1 : 0;
    if (doneA !== doneB) return doneA - doneB;
    if (a.dueDate !== b.dueDate) {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate < b.dueDate ? -1 : 1;
    }
    const byPriority = priorityWeight[a.priority] - priorityWeight[b.priority];
    if (byPriority !== 0) return byPriority;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** «Cada 2 semanas (lun, jue)» in the interface language (`recurrenceLabel` is Spanish only). */
export function recurrenceText(r: Recurrence): string {
  const n = r.interval;
  const base = {
    daily: n === 1 ? t('Cada día') : t('Cada {{n}} días', { n }),
    weekly: n === 1 ? t('Cada semana') : t('Cada {{n}} semanas', { n }),
    monthly: n === 1 ? t('Cada mes') : t('Cada {{n}} meses', { n }),
  }[r.frequency];
  if (r.frequency !== 'weekly' || r.weekdays.length === 0) return base;
  // 2023-01-01 was a Sunday, so day `d` (0 = Sunday) is 2023-01-0(d+1).
  const weekday = new Intl.DateTimeFormat(intlLocale(), { weekday: 'short', timeZone: 'UTC' });
  const days = [...r.weekdays]
    .sort()
    .map((d) => weekday.format(new Date(Date.UTC(2023, 0, 1 + d))))
    .join(', ');
  return `${base} (${days})`;
}

export interface TaskFilters {
  assigneeId?: string | null;
  channelId?: string | null;
  priority?: TaskPriority | null;
  query?: string;
  includeDone?: boolean;
  /** The team's task key, so the query also matches keys like `NEX-42`. */
  taskKey?: string;
}

export function filterTasks(tasks: Task[], filters: TaskFilters): Task[] {
  const q = filters.query?.trim().toLowerCase() ?? '';
  return tasks.filter((task) => {
    if (filters.includeDone === false && task.status === 'done') return false;
    if (filters.assigneeId && !task.assignees.some((a) => a.id === filters.assigneeId))
      return false;
    if (filters.channelId && task.channelId !== filters.channelId) return false;
    if (filters.priority && task.priority !== filters.priority) return false;
    const key = filters.taskKey ? `${filters.taskKey}-${task.number}` : '';
    if (q && !`${key} ${task.title} ${task.description ?? ''}`.toLowerCase().includes(q))
      return false;
    return true;
  });
}

export function dueTone(task: Task, now = new Date()): BadgeTone {
  if (!task.dueDate || task.status === 'done') return 'neutral';
  const state = dueState(task.dueDate, now);
  if (state === 'overdue') return 'danger';
  if (state === 'today' || state === 'soon') return 'warning';
  return 'neutral';
}

export interface TaskGroup {
  key: string;
  label: string;
  tasks: Task[];
  tone?: BadgeTone;
  /** Person or conversation the group represents, for richer headers. */
  person?: UserSummary;
  channel?: Channel;
  status?: TaskStatus;
  priority?: TaskPriority;
}

export type GroupBy = 'status' | 'assignee' | 'channel' | 'priority';

/** Spanish keys: translate at render with `t()`. */
export const groupByLabels: Record<GroupBy, string> = {
  status: 'Estado',
  assignee: 'Responsable',
  channel: 'Conversación',
  priority: 'Prioridad',
};

/** Buckets for "Mis tareas", ordered by urgency. */
type DueBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none' | 'done';
const dueBuckets: { key: DueBucket; label: string; tone?: BadgeTone }[] = [
  { key: 'overdue', label: 'Vencidas', tone: 'danger' },
  { key: 'today', label: 'Hoy', tone: 'warning' },
  { key: 'tomorrow', label: 'Mañana' },
  { key: 'week', label: 'Próximos 7 días' },
  { key: 'later', label: 'Más adelante' },
  { key: 'none', label: 'Sin fecha' },
  { key: 'done', label: 'Completadas' },
];

export function dueBucket(task: Task, now = new Date()): DueBucket {
  if (task.status === 'done') return 'done';
  if (!task.dueDate) return 'none';
  const diff = daysFromToday(parseDueDate(task.dueDate), now);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff <= 7) return 'week';
  return 'later';
}

export function groupByDue(tasks: Task[], now = new Date()): TaskGroup[] {
  const sorted = sortTasks(tasks);
  return dueBuckets
    .map(({ key, label, tone }) => ({
      key,
      label: t(label),
      tasks: sorted.filter((task) => dueBucket(task, now) === key),
      ...(tone ? { tone } : {}),
    }))
    .filter((g) => g.tasks.length > 0);
}

/** Groups tasks by a property. Empty groups are dropped except status columns. */
export function groupTasks(
  tasks: Task[],
  by: GroupBy,
  ctx: { channels: Channel[]; members: UserSummary[] },
): TaskGroup[] {
  const sorted = sortTasks(tasks);
  switch (by) {
    case 'status':
      return taskStatuses.map((status) => ({
        key: status,
        label: t(taskStatusLabels[status]),
        status,
        tasks: sorted.filter((task) => task.status === status),
      }));
    case 'priority':
      return taskPriorities
        .map((priority) => ({
          key: priority,
          label: t(taskPriorityLabels[priority], { context: 'priority' }),
          priority,
          tasks: sorted.filter((task) => task.priority === priority),
        }))
        .filter((g) => g.tasks.length > 0);
    case 'assignee': {
      const groups: TaskGroup[] = ctx.members.map((person) => ({
        key: person.id,
        label: person.displayName,
        person,
        tasks: sorted.filter((task) => task.assignees.some((a) => a.id === person.id)),
      }));
      groups.push({
        key: 'nobody',
        label: t('Sin asignar'),
        tasks: sorted.filter((task) => task.assignees.length === 0),
      });
      return groups.filter((g) => g.tasks.length > 0);
    }
    case 'channel': {
      const groups: TaskGroup[] = ctx.channels.map((channel) => ({
        key: channel.id,
        label:
          channel.kind === 'dm'
            ? (channel.dmPeer?.displayName ?? t('Mensaje directo'))
            : `#${channel.name}`,
        channel,
        tasks: sorted.filter((task) => task.channelId === channel.id),
      }));
      groups.push({
        key: 'none',
        label: t('Sin conversación'),
        tasks: sorted.filter((task) => !task.channelId),
      });
      return groups.filter((g) => g.tasks.length > 0);
    }
  }
}

const defaultColumnNames = new Set(defaultTaskColumns.map((column) => column.name));

/**
 * A board column's name as shown: the default columns every team starts with («Pendiente»,
 * «En curso», «Hecha») are stored in Spanish and translated; names people chose are left alone.
 */
export const columnName = (name: string): string => (defaultColumnNames.has(name) ? t(name) : name);
